import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Form, Grid, Modal, Input, InputOnChangeData, Icon, Dropdown } from "semantic-ui-react";

import { toast } from "@teamprevent/common/toasts";

import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";

import { ApplicationState } from "../../../../applicationState";
import { EmployeeRecordDetailModel, EmployeeRecordSearchModel, MergeEmployeeRecordModel } from "../../model";
import { mergeEmployeeRecord } from "../../actions";
import { employeeRecordApi } from "../..";

interface Props {
    isOpen: boolean;
    onMergeComplete: () => void;
    employeeRecord: EmployeeRecordDetailModel;
}

export const MergeEmployeeRecordModal: React.FunctionComponent<Props> = ({ isOpen, onMergeComplete, employeeRecord }) => {
    const dispatch = useDispatch();
    const customerId = useSelector((state: ApplicationState) => state.router?.params!.customerId);
    const fromEmployeeRecordId = useSelector((state: ApplicationState) => state.router?.params!.employeeRecordId);

    const [searchEmployeeName, setSearchEmployeeName] = React.useState<string | undefined>(undefined);
    const [foundEmployeeRecords, setFoundEmployeeRecords] = React.useState<EmployeeRecordSearchModel[]>([]);
    const [selectedToEmployeeRecord, setSelectedToEmployeeRecord] = React.useState<EmployeeRecordSearchModel | undefined>(undefined);

    const foundEmployeeRecordsOptions = React.useMemo(() => foundEmployeeRecords.map(e => ({
        text: `${e.firstName} ${e.lastName} - ${e.dateOfBirth} - ${e.personalReferenceNumber}`,
        value: e.id
    })), [foundEmployeeRecords]);

    const resetState = React.useCallback(() => {
        setSearchEmployeeName(undefined);
        setFoundEmployeeRecords([]);
    },[]);

    const onCancel = React.useCallback(() => {
        resetState();
        onMergeComplete();
    }, [onMergeComplete, resetState]);

    const onConfirm = React.useCallback(() => {
        if (fromEmployeeRecordId === selectedToEmployeeRecord?.id) {
            toast.warning("Cannot merge the employee record to itself");
            return;
        }

        if (!selectedToEmployeeRecord?.id) {
            toast.info("Search for employee record or choose to create a new one");
            return;
        }

        const requestModel: MergeEmployeeRecordModel = {
            customerId,
            fromEmployeeRecordId,
            toEmployeeRecordId: selectedToEmployeeRecord.id
        };

        dispatch(mergeEmployeeRecord(requestModel));
        resetState();
        toast.success("Merging process started.");
        onMergeComplete();
    }, [customerId, dispatch, fromEmployeeRecordId, onMergeComplete, resetState, selectedToEmployeeRecord]);

    const searchForEmployee = React.useCallback(async () => {
        if (searchEmployeeName) {
            const result = await employeeRecordApi.searchEmployeeRecords(customerId, searchEmployeeName);
            setFoundEmployeeRecords(result.filter(e => e.id !== fromEmployeeRecordId));
        }
    }, [customerId, fromEmployeeRecordId, searchEmployeeName]);

    const onEmployeeRecordSearchMergeChange = React.useCallback((event: React.SyntheticEvent<HTMLElement>, { value }: any) => {
        setSelectedToEmployeeRecord(foundEmployeeRecords.find(e => e.id === value));
    }, [foundEmployeeRecords]);

    const onSetSearchEmployeeName = React.useCallback((_e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setSearchEmployeeName(data.value);
    }, [setSearchEmployeeName]);

    const buildPromptText = React.useMemo(() => {
    /* eslint-disable max-len */
        if (!selectedToEmployeeRecord) {
            return "";
        }

        const fromEmployeeRecordDetails = `${employeeRecord.firstName} ${employeeRecord.lastName} | ${employeeRecord.personalReferenceNumber}`;
        const toEmployeeRecordDetails = `${selectedToEmployeeRecord.firstName} ${selectedToEmployeeRecord.lastName} | ${selectedToEmployeeRecord.personalReferenceNumber}`;

        let promptText = `Merging this record (${fromEmployeeRecordDetails}) into target record (${toEmployeeRecordDetails}) will perform the following:\n`;

        promptText += "\n- Reset the email on this record for future use.";
        promptText += "\n- Move cases, tasks, immunisations and documents from this record to the target record.";
        promptText += "\n\nDo you wish to continue? Type 'MERGE' and click confirm to merge these records.\n\n";

        return promptText;
    /* eslint-enable max-len */
    }, [employeeRecord, selectedToEmployeeRecord]);

    return (
        <>
            <Modal open={isOpen} size="small">
                <Modal.Header content={"Merge Employee Records"} />
                <Modal.Content>
                    <Form>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column>
                                    <strong>Search Employee Name</strong>
                                    <Input
                                        icon={<Icon name="search" link onClick={searchForEmployee} />}
                                        onChange={onSetSearchEmployeeName}
                                        fluid />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column>
                                    <label>Target Employee Record</label>
                                    <Dropdown
                                        value={selectedToEmployeeRecord?.id}
                                        onChange={onEmployeeRecordSearchMergeChange}
                                        options={foundEmployeeRecordsOptions}
                                        search
                                        fluid
                                        selection
                                        labeled
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={onCancel} content="Cancel" />
                    <ConfirmedButton
                        content="Confirm"
                        onClick={onConfirm}
                        promptText={buildPromptText}
                        typeToConfirmText="MERGE"
                        headingText="Merge Employees"
                        modalClassName="with-line-breaks"
                        confirmButtonColor={"green"}
                        modalSize="small"
                        disabled={selectedToEmployeeRecord === undefined}
                    />
                </Modal.Actions>
            </Modal>
        </>
    );
};
